import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SEOSchema from '@/components/feature/SEOSchema';
import { aboutInfo } from '@/mocks/comics';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, value as string);
        }
      });
      const response = await fetch('https://readdy.ai/api/form/d9orkaqqs38sj6jc7e80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(msg);
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫大全 | 无删减韩漫在线观看平台"
        description="了解韩漫大全品牌故事、口碑评价与联系方式。国内领先的韩国漫画在线阅读平台，5000+精选韩漫作品，正版授权无删减。"
        keywords="韩漫网站,无删减韩漫在线观看,漫画韩漫网址,免费韩国漫画大全,关于我们"
        canonicalUrl="https://www.manhuakr.com/about"
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: '关于我们 - 韩漫大全',
          description: '了解韩漫大全品牌故事、口碑评价与联系方式',
          url: 'https://www.manhuakr.com/about',
          inLanguage: 'zh-CN',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />

            {/* Hero */}
            <section className="text-center py-10 md:py-16">
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-50 mb-4">
                关于韩漫大全
              </h1>
              <p className="text-foreground-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.slogan}
              </p>
            </section>

            {/* Brand Introduction */}
            <section className="flex flex-col lg:flex-row gap-8 mb-14">
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-4">
                  品牌介绍
                </h2>
                <p className="text-foreground-400 text-sm leading-relaxed mb-4">
                  {aboutInfo.introduction}
                </p>
                <p className="text-foreground-400 text-sm leading-relaxed">
                  <strong className="text-foreground-200">我们的使命：</strong>
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-96">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20workspace%20with%20webtoon%20illustrations%20on%20walls%2C%20digital%20drawing%20tablets%20on%20desks%2C%20warm%20ambient%20lighting%2C%20Korean%20comic%20studio%20atmosphere%2C%20artistic%20creative%20space%2C%20cozy%20modern%20interior&width=800&height=500&seq=about-brand-manhwa&orientation=landscape"
                  alt="韩漫大全品牌"
                  title="韩漫大全 - 品牌形象"
                  className="w-full h-64 lg:h-full object-cover rounded-lg"
                />
              </div>
            </section>

            {/* Stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
              {aboutInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <p className="text-3xl md:text-4xl font-black text-primary-400 mb-1 font-heading">
                    {stat.value}
                  </p>
                  <p className="text-foreground-500 text-xs">{stat.label}</p>
                </div>
              ))}
            </section>

            {/* Values */}
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 text-center mb-8">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {aboutInfo.values.map((val) => (
                  <div
                    key={val.title}
                    className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 mb-4">
                      <i className="ri-check-double-line text-lg"></i>
                    </div>
                    <h3 className="text-foreground-50 font-semibold text-sm mb-2">
                      {val.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      {val.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 text-center mb-8">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { name: '漫画迷小王', text: '韩漫大全真的太棒了！翻译质量很高，更新速度也快，已经成为我每天必刷的网站了。', rating: 5 },
                  { name: '追漫少女', text: '界面干净整洁，没有乱七八糟的广告，阅读体验一级棒。强烈推荐给喜欢韩漫的朋友！', rating: 5 },
                  { name: '二次元宅', text: '收藏了很多韩漫作品，分类很清晰，找漫画非常方便。希望能引进更多新作品！', rating: 4 },
                ].map((review, index) => (
                  <div
                    key={index}
                    className="p-5 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-600'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-foreground-400 text-sm leading-relaxed mb-3">
                      "{review.text}"
                    </p>
                    <p className="text-foreground-200 text-xs font-medium">
                      — {review.name}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 text-center mb-8">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {aboutInfo.faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 text-foreground-50 font-medium text-sm list-none">
                      {faq.question}
                      <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                    </summary>
                    <div className="px-5 pb-4 text-foreground-400 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <SEOSchema
              type="FAQPage"
              data={{
                mainEntity: aboutInfo.faqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: { '@type': 'Answer', text: faq.answer },
                })),
              }}
            />

            {/* Contact */}
            <section className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-400 text-sm leading-relaxed mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们将尽快回复。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-foreground-300 text-xs">邮箱</p>
                      <p className="text-foreground-50">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-400">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-foreground-300 text-xs">微信</p>
                      <p className="text-foreground-50">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-500/10 text-secondary-400">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-foreground-300 text-xs">地址</p>
                      <p className="text-foreground-50">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex-1">
                <div className="bg-background-100 rounded-lg border border-background-200/70 p-6">
                  <h3 className="text-foreground-50 font-semibold text-sm mb-4">
                    发送消息
                  </h3>
                  <form onSubmit={handleSubmit} data-readdy-form>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-foreground-300 text-xs mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-50 text-sm focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-300 text-xs mb-1.5">邮箱</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-50 text-sm focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div className="antispam-field">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-300 text-xs mb-1.5">消息</label>
                        <textarea
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-50 text-sm focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                          placeholder="请输入您的消息（不超过500字）"
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-background-50 py-2.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer"
                      >
                        {formState === 'submitting' ? '发送中...' : '发送消息'}
                      </button>
                      {formState === 'success' && (
                        <p className="text-accent-400 text-xs text-center">
                          消息已发送成功，感谢您的留言！
                        </p>
                      )}
                      {formState === 'error' && (
                        <p className="text-red-400 text-xs text-center">
                          {formError}
                        </p>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}